import React, { useState, useEffect } from 'react';
import ManualEntryTab from './ManualEntryTab';
import ManagePropertiesTab from './ManagePropertiesTab';
import NeighborhoodManagementTab from './NeighborhoodManagementTab';
import LoginForm from './LoginForm';
import propertyService from '../../services/propertyService';
import './AdminDashboard.css';
import CreateAgentForm from './CreateAgentForm';
import ReviewListingsTab from './ReviewListingsTab';
import ShowingManagementTab from './ShowingManagementTab';
import UserAssignmentManager from './UserAssignmentManager';
import UserMetricsPanel from './UserMetricsPanel';
import AgentMetricsPanel from './AgentMetricsPanel';
import PriorityQueuePanel from './PriorityQueuePanel';
import NotificationQueuePanel from './NotificationQueuePanel';
import AdminMessagesPanel from './AdminMessagesPanel';
import AgentMessagesPanel from './AgentMessagesPanel';
import AdminMessaging from './AdminMessaging';
import CuratedMatchesManager from './CuratedMatchesManager';
import PoolManagement from './PoolManagement';
import AdminConversations from './AdminConversations';
import AgentMonitoring from './AgentMonitoring';
import ReturnRequestsPanel from './ReturnRequestsPanel';
import ConditionLabeler from './ConditionLabeler';
import VisionTest from './VisionTest';
import AvmReadiness from './AvmReadiness';
import AvmEstimate from './AvmEstimate';
import ConditionByMetro from './ConditionByMetro';
import VideoJobsManager from './VideoJobsManager';
import StudioCampaigns from './StudioCampaigns';
import AgentListingsTab from './AgentListingsTab';


const PropertyAdminDashboard = () => {
  // State Management
  const [activeTab, setActiveTab] = useState('overview');
  const [propertyCount, setPropertyCount] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [showingCount, setShowingCount] = useState(0);
  const [totalSwipes, setTotalSwipes] = useState(0);
  const [totalClaimedUsers, setTotalClaimedUsers] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalAgents, setTotalAgents] = useState(0);
  const [totalAgentMessages, setTotalAgentMessages] = useState(0);
  // User tier distribution for the pie chart { flex, bronze, silver, gold, platinum }
  const [tierDistribution, setTierDistribution] = useState({ flex: 0, bronze: 0, silver: 0, gold: 0, platinum: 0 });
  // Real (not approximated) count of users with at least one like or dislike
  const [usersWhoSwiped, setUsersWhoSwiped] = useState(null);
  const [topPools, setTopPools] = useState([]); // [{name, count, state}]
  const [allPools, setAllPools] = useState([]); // full list for search/bookmarks
  const [poolSearch, setPoolSearch] = useState('');
  const [bookmarkedPools, setBookmarkedPools] = useState(() => {
    try {
      const saved = localStorage.getItem('bookmarked_pools');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    properties: true,
    agents: true,
    users: true,
    archived: false,
  });
  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  // Persist bookmarked pools across sessions
  useEffect(() => {
    try {
      localStorage.setItem('bookmarked_pools', JSON.stringify(bookmarkedPools));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [bookmarkedPools]);

  const isPoolBookmarked = (id) => bookmarkedPools.some(p => p._id === id);

  const toggleBookmarkPool = (pool) => {
    setBookmarkedPools(prev =>
      prev.some(p => p._id === pool._id)
        ? prev.filter(p => p._id !== pool._id)
        : [...prev, { _id: pool._id, name: pool.name }]
    );
  };

  // Generate particles for background animation
  const generateParticles = () => {
    const particles = [];
    for (let i = 0; i < 30; i++) {
      const size = Math.random() * 10 + 4; // Slightly larger particles (4-14px)
      const left = Math.random() * 100;
      const delay = Math.random() * 10; // Reduced delay (0-10s)
      const duration = Math.random() * 10 + 15; // Faster duration (15-25s)
      
      particles.push({
        size,
        left,
        delay,
        duration
      });
    }
    return particles;
  };

  const particles = generateParticles();

  // Check authentication and load theme preference
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      setIsAuthenticated(true);
    }
    
    // Light mode only for now — clear any stale dark theme state
    document.documentElement.removeAttribute('data-theme');
    localStorage.setItem('theme', 'light');
    setIsDarkMode(false);
    
    setIsCheckingAuth(false);
  }, []);
  
  // Fetch all counts when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setMetricsLoading(true);
      Promise.allSettled([
        fetchPropertyCount(),
        fetchShowingCount(),
        fetchPendingReviewCount(),
        fetchTotalSwipes(),
        fetchTotalClaimedUsers(),
        fetchTotalUsers(),
        fetchTotalAgents(),
        fetchTotalAgentMessages(),
        fetchTopPools(),
        fetchTierDistribution(),
        fetchUsersWhoSwiped(),
      ]).finally(() => setMetricsLoading(false));
    }
  }, [isAuthenticated]);

  // Dark mode toggle
  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    
    if (newMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
      localStorage.setItem('theme', 'light');
    }
  };
  
  const fetchPropertyCount = async () => {
    // Try the property service first (tolerant of multiple response shapes),
    // fall back to a direct API hit that reads only the paginated `total`.
    try {
      const response = await propertyService.getPropertyCount();
      // Shape-tolerant: handles {count}, {data:{count}}, {total}, {data:{total}}, etc.
      let count = null;
      if (response) {
        if (typeof response.count === 'number') count = response.count;
        else if (typeof response.total === 'number') count = response.total;
        else if (response.data && typeof response.data.count === 'number') count = response.data.count;
        else if (response.data && typeof response.data.total === 'number') count = response.data.total;
      }
      if (count !== null) {
        setPropertyCount(count);
        return;
      }
      // Service returned a funky shape — fall through to the fallback below
    } catch (error) {
      console.warn('propertyService.getPropertyCount failed, falling back:', error?.message);
    }

    // Fallback: hit the properties list endpoint with limit=1 just to read `total`.
    // Avoids pulling actual property bodies (no crash risk).
    try {
      const API = 'https://back-end-x7tx.onrender.com/api';
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/properties?page=1&limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const json = await res.json();
      // Try common pagination shapes
      let count = null;
      if (typeof json.total === 'number') count = json.total;
      else if (typeof json.count === 'number') count = json.count;
      else if (json.pagination && typeof json.pagination.total === 'number') count = json.pagination.total;
      else if (Array.isArray(json.data)) count = json.data.length;
      if (count !== null) setPropertyCount(count);
    } catch (err) {
      console.error('Error fetching property count (fallback):', err);
    }
  };

  const fetchShowingCount = async () => {
    try {
      const response = await propertyService.getShowingCount();
      if (response && response.count !== undefined) {
        setShowingCount(response.count);
      }
    } catch (error) {
      console.error('Error fetching showing count:', error);
    }
  };

  const fetchPendingReviewCount = async () => {
    try {
      const response = await propertyService.getProperties({ 
        propertyStatus: 'Pending Admin Review' 
      });
      
      if (response && response.data) {
        setPendingReviewCount(response.data.length);
      } else if (Array.isArray(response)) {
        setPendingReviewCount(response.length);
      } else {
        setPendingReviewCount(0);
      }
    } catch (error) {
      console.error('Error fetching pending review count:', error);
      setPendingReviewCount(0);
    }
  };

  const fetchTotalSwipes = async () => {
    try {
      console.log('🔍 Fetching total swipes...');
      const response = await propertyService.getTotalSwipes();
      console.log('✅ Total swipes response:', response);
      if (response && response.data && response.data.count !== undefined) {
        console.log('📊 Setting total swipes to:', response.data.count);
        setTotalSwipes(response.data.count);
      } else {
        console.warn('⚠️ Invalid swipes response structure:', response);
      }
    } catch (error) {
      console.error('❌ Error fetching total swipes:', error);
    }
  };

  const fetchTotalUsers = async () => {
    try {
      const API = 'https://back-end-x7tx.onrender.com/api';
      const token = localStorage.getItem('auth_token');
      // Count via the admin users endpoint (returns `total` in paginated shape).
      // Fall back to counting the returned array if `total` isn't present.
      const res = await fetch(`${API}/users/with-assignments?page=1&limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const json = await res.json();
      if (typeof json.total === 'number') setTotalUsers(json.total);
      else if (Array.isArray(json.data)) setTotalUsers(json.data.length);
    } catch (err) {
      console.error('Error fetching total users:', err);
    }
  };

  const fetchTotalAgents = async () => {
    // "Active Agents" KPI — counts agents where agentStatus === 'active'
    // (i.e. not paused/past_due/inactive). Uses the same /admin/agents endpoint
    // the rest of the admin panel uses, then filters client-side by status.
    try {
      const API = 'https://back-end-x7tx.onrender.com/api';
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/admin/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const json = await res.json();
      // Tolerant response shape: {data: [...]}, {agents: [...]}, or plain array
      const list = Array.isArray(json) ? json : (json.data || json.agents || []);
      if (!Array.isArray(list)) {
        setTotalAgents(0);
        return;
      }
      // Count only agents whose status is 'active'. Treat missing status as active
      // (legacy agents created before the field existed default to active on next save).
      const activeCount = list.filter(a => (a.agentStatus || 'active') === 'active').length;
      setTotalAgents(activeCount);
    } catch (err) {
      console.error('Error fetching total agents:', err);
    }
  };

  const fetchTopPools = async () => {
    try {
      const API = 'https://back-end-x7tx.onrender.com/api';
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/agent-pools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const json = await res.json();
      const pools = Array.isArray(json.data) ? json.data : [];
      const mapped = pools.map(p => ({
        _id: p._id,
        name: p.name,
        state: p.serviceArea?.state || null,
        city: p.serviceArea?.city || null,
        count: (p.agents && p.agents.length) || 0,
      }));
      // Full list (incl. empty pools) for search + bookmarks, alpha-sorted
      setAllPools([...mapped].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      // Rank by agent count desc, drop empty pools, take top 20 for the chart
      const ranked = mapped
        .filter(p => p.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);
      setTopPools(ranked);
    } catch (err) {
      console.error('Error fetching top pools:', err);
    }
  };

  const fetchUsersWhoSwiped = async () => {
    // Counts users where likes or dislikes is non-empty.
    // Single-hit, reads only the paginated `total` field.
    try {
      const API = 'https://back-end-x7tx.onrender.com/api';
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/users/with-assignments?page=1&limit=1&hasSwiped=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const json = await res.json();
      if (typeof json.total === 'number') setUsersWhoSwiped(json.total);
    } catch (err) {
      console.error('Error fetching users who swiped:', err);
    }
  };

  const fetchTierDistribution = async () => {
    // Counts users by tier (flex/bronze/silver/gold/platinum).
    // Fires one parallel count-only query per tier — each returns just the `total`
    // field, no document bodies, so it's cheap even at scale.
    try {
      const API = 'https://back-end-x7tx.onrender.com/api';
      const token = localStorage.getItem('auth_token');
      const tiers = ['flex', 'bronze', 'silver', 'gold', 'platinum'];
      const results = await Promise.all(
        tiers.map(tier =>
          fetch(`${API}/users/with-assignments?page=1&limit=1&leadTier=${tier}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
            .then(r => r.ok ? r.json() : null)
            .then(j => j && typeof j.total === 'number' ? j.total : 0)
            .catch(() => 0)
        )
      );
      // Zip tiers -> counts by name so adding a tier can't silently misalign.
      setTierDistribution(
        tiers.reduce((acc, tier, i) => ({ ...acc, [tier]: results[i] }), {})
      );
    } catch (err) {
      console.error('Error fetching tier distribution:', err);
    }
  };

  const fetchTotalAgentMessages = async () => {
    // Sums the messageCount across all agent conversations.
    // Uses /api/admin/agent-conversations (already exists per our messaging work).
    try {
      const API = 'https://back-end-x7tx.onrender.com/api';
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/admin/agent-conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const json = await res.json();
      const conversations = Array.isArray(json.data) ? json.data : [];
      const total = conversations.reduce((sum, c) => sum + (c.messageCount || 0), 0);
      setTotalAgentMessages(total);
    } catch (err) {
      console.error('Error fetching total agent messages:', err);
    }
  };

  const fetchTotalClaimedUsers = async () => {
    try {
      console.log('🔍 Fetching total claimed users...');
      const response = await propertyService.getTotalClaimedUsers();
      console.log('✅ Total claimed users response:', response);
      if (response && response.data && response.data.count !== undefined) {
        console.log('📊 Setting total claimed users to:', response.data.count);
        setTotalClaimedUsers(response.data.count);
      } else {
        console.warn('⚠️ Invalid claimed users response structure:', response);
      }
    } catch (error) {
      console.error('❌ Error fetching total claimed users:', error);
    }
  };
  
  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    setIsAuthenticated(false);
    window.location.href = '/';
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  // Show loading state while checking auth
  if (isCheckingAuth) {
    return (
      <div className="admin-dashboard">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="dashboard-title">Property Data Management</h1>
          </div>
        </div>
        <div className="content-container">
          <div className="text-center">
            <div className="skeleton skeleton-card" style={{ maxWidth: '400px', margin: '2rem auto' }}></div>
            <div className="skeleton skeleton-text" style={{ maxWidth: '300px', margin: '1rem auto' }}></div>
          </div>
        </div>
      </div>
    );
  }
  
  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-dashboard">
        {/* Geometric Background */}
        <div className="geometric-background">
          <div className="geometric-shape circle-1"></div>
          <div className="geometric-shape circle-2"></div>
          <div className="geometric-shape circle-3"></div>
          <div className="geometric-shape hexagon-1"></div>
          <div className="geometric-shape square-1"></div>
          <div className="geometric-shape square-2"></div>
          <div className="geometric-line line-1"></div>
          <div className="geometric-line line-2"></div>
          <div className="geometric-line line-3"></div>
        </div>

        {/* Floating Particles */}
        <div className="floating-particles">
          {particles.map((particle, index) => (
            <div
              key={index}
              className="particle"
              style={{
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                left: `${particle.left}%`,
                animationDelay: `${particle.delay}s`,
                animationDuration: `${particle.duration}s`
              }}
            />
          ))}
        </div>

        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="dashboard-title">Property Data Management</h1>
            <p className="dashboard-subtitle">Secure admin access</p>
          </div>
        </div>
        <div style={{ padding: '2rem' }}>
          <LoginForm onLoginSuccess={handleLoginSuccess} />
        </div>
      </div>
    );
  }

  // Navigation items configuration — grouped with collapsible folders
  const dashboardItem = {
    id: 'overview',
    icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>,
    label: 'Dashboard',
    badge: null,
  };

  const navGroups = [
    {
      key: 'agents',
      label: 'Agents',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
      items: [
        {
          id: 'agents',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
          label: 'Agent Management',
          badge: null,
        },
        {
          id: 'agent-metrics',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>,
          label: 'Agent Metrics',
          badge: null,
        },
        {
          id: 'agent-messages',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>,
          label: 'Agent Messages',
          badge: null,
        },
        {
          id: 'pools',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path><line x1="2" y1="12" x2="22" y2="12"></line></svg>,
          label: 'Agent Pools',
          badge: null,
        },
        {
          id: 'return-requests',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 14 4 9 9 4"></polyline><path d="M20 20v-7a4 4 0 0 0-4-4H4"></path></svg>,
          label: 'Return Requests',
          badge: null,
        },
        {
          id: 'priority-queue',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"></line><line x1="4" y1="10" x2="4" y2="3"></line><line x1="12" y1="21" x2="12" y2="12"></line><line x1="12" y1="8" x2="12" y2="3"></line><line x1="20" y1="21" x2="20" y2="16"></line><line x1="20" y1="12" x2="20" y2="3"></line><line x1="1" y1="14" x2="7" y2="14"></line><line x1="9" y1="8" x2="15" y2="8"></line><line x1="17" y1="16" x2="23" y2="16"></line></svg>,
          label: 'Priority Queue',
          badge: null,
        },
        {
          id: 'notification-queue',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>,
          label: 'Notification Queue',
          badge: null,
        },
      ],
    },
    {
      key: 'users',
      label: 'Users',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
      items: [
        {
          id: 'user-assignments',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
          label: 'User Management',
          badge: null,
        },
        {
          id: 'user-metrics',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>,
          label: 'User Metrics',
          badge: null,
        },
        {
          id: 'user-messages',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>,
          label: 'User Messages',
          badge: null,
        },
      ],
    },
    {
      key: 'properties',
      label: 'Properties',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>,
      items: [
        {
          id: 'agent-listings',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"></path><path d="M5 21V7l8-4v18"></path><path d="M19 21V11l-6-4"></path><line x1="9" y1="9" x2="9" y2="9"></line><line x1="9" y1="12" x2="9" y2="12"></line><line x1="9" y1="15" x2="9" y2="15"></line></svg>,
          label: 'Agent Listings',
          badge: null,
        },
        {
          id: 'video-jobs',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>,
          label: 'Video Jobs',
          badge: null,
        },
      ],
    },
    {
      key: 'studio',
      label: 'Studio',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>,
      items: [
        {
          id: 'studio-campaigns',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>,
          label: 'Campaigns',
          badge: null,
        },
      ],
    },
    {
      key: 'archived',
      label: 'Archived',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg>,
      items: [
        {
          id: 'entry',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
          label: 'Manual Entry',
          badge: null,
        },
        {
          id: 'manage',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>,
          label: 'Manage Properties',
          badge: null,
        },
        {
          id: 'reviews',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
          label: 'Review Listings',
          badge: null,
        },
        {
          id: 'showings',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
          label: 'Manage Showings',
          badge: null,
        },
        {
          id: 'neighborhoods',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>,
          label: 'Neighborhoods',
          badge: null,
        },
        {
          id: 'agent-monitoring',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
          label: 'Agent Monitoring',
          badge: null,
        },
        {
          id: 'conversations',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>,
          label: 'Conversations',
          badge: null,
        },
        {
          id: 'messages',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>,
          label: 'Messages',
          badge: null,
        },
        {
          id: 'curated-matches',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>,
          label: 'Curated Matches',
          badge: null,
        },
        {
          id: 'condition-labeling',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>,
          label: 'Condition Labeling',
          badge: null,
        },
        {
          id: 'vision-test',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>,
          label: 'Vision Test',
          badge: null,
        },
        {
          id: 'avm-readiness',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>,
          label: 'AVM Readiness',
          badge: null,
        },
        {
          id: 'avm-estimate',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>,
          label: 'AVM Estimate',
          badge: null,
        },
        {
          id: 'condition-by-metro',
          icon: <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>,
          label: 'Condition by Metro',
          badge: null,
        },
      ],
    },
  ];
  
  // Main authenticated dashboard
  return (
    <div className="dashboard-container">
      {/* Floating Particles */}
      <div className="floating-particles">
        {particles.map((particle, index) => (
          <div
            key={index}
            className="particle"
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              left: `${particle.left}%`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`
            }}
          />
        ))}
      </div>

      {/* Floating Sidebar */}
      <aside 
        className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}
      >
        {/* Sidebar Header */}
        <div className="logo-container">
          <img 
            src={require('../../assets/LogoWhiteCropped.png')}
            alt="Lauft Logo" 
            className="logo-image"
            style={{ filter: 'invert(1) brightness(0.1)' }}
          />
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <ul>
            {/* Dashboard (standalone top-level item) */}
            <li
              className={`nav-item ${activeTab === dashboardItem.id ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(dashboardItem.id);
                setMobileMenuOpen(false);
              }}
            >
              <span className="icon">{dashboardItem.icon}</span>
              <span>{dashboardItem.label}</span>
            </li>

            {/* Grouped sections with collapsible children */}
            {navGroups.map((group) => {
              const isExpanded = expandedGroups[group.key];
              const hasActiveChild = group.items.some(i => i.id === activeTab);
              return (
                <React.Fragment key={group.key}>
                  <li
                    className={`nav-group-header ${hasActiveChild ? 'has-active' : ''}`}
                    onClick={() => toggleGroup(group.key)}
                  >
                    <span className="icon">{group.icon}</span>
                    <span className="group-label">{group.label}</span>
                    <span className={`chevron ${isExpanded ? 'open' : ''}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </li>
                  {isExpanded && group.items.map((item) => (
                    <li
                      key={item.id}
                      className={`nav-item nav-sub-item ${activeTab === item.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab(item.id);
                        setMobileMenuOpen(false);
                      }}
                    >
                      <span className="icon">{item.icon}</span>
                      <span>{item.label}</span>
                      {item.badge && <span className="nav-badge">{item.badge}</span>}
                    </li>
                  ))}
                </React.Fragment>
              );
            })}

          </ul>
        </nav>

        {/* Sidebar footer: brand mark + version/date + logout (pinned to bottom) */}
        <div className="sidebar-footer">
          <div className="sidebar-brand">
            <img src="/lauft-logo.png" alt="Lauft" className="sidebar-brand-logo" />
            <div className="sidebar-brand-meta">
              <div className="sidebar-brand-version">Portal v3.0.1</div>
              <div className="sidebar-brand-date">
                {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          </div>
          <button className="sidebar-logout" onClick={handleLogout} type="button">
            <span className="icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Dashboard Overview */}
        {activeTab === 'overview' && (() => {
          // Derived metrics
          const claimRate = totalUsers > 0 ? (totalClaimedUsers / totalUsers * 100) : 0;
          const avgSwipesPerUser = totalUsers > 0 ? Math.round(totalSwipes / totalUsers) : 0;
          const unclaimedUsers = Math.max(0, totalUsers - totalClaimedUsers);
          const maxPoolCount = topPools.length > 0 ? Math.max(...topPools.map(p => p.count), 1) : 1;

          // Compact number formatter (1.2k, 3.4M)
          const fmt = (n) => {
            if (n === null || n === undefined) return '—';
            if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
            if (n >= 10000) return Math.round(n / 1000) + 'k';
            if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
            return n.toLocaleString();
          };

          // KPI card config
          const kpis = [
            { label: 'Total Swipes', value: totalSwipes.toLocaleString(), full: totalSwipes.toLocaleString() },
            { label: 'Total Users', value: totalUsers.toLocaleString(), full: totalUsers.toLocaleString(), onClick: () => setActiveTab('user-assignments') },
            { label: 'Claimed Users', value: fmt(totalClaimedUsers), full: totalClaimedUsers.toLocaleString(), onClick: () => setActiveTab('user-assignments') },
            { label: 'Active Agents', value: fmt(totalAgents), full: totalAgents.toLocaleString(), onClick: () => setActiveTab('agents') },
            { label: 'Agent Messages Sent', value: fmt(totalAgentMessages), full: totalAgentMessages.toLocaleString() },
          ];

          return (
            <div className="dashboard-content">
              {/* Wordmark header */}
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px'}}>
                <img src="/lauft-logo.png" alt="Lauft" style={{width: '22px', height: '22px', objectFit: 'contain'}} />
                <span style={{fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', color: '#888', textTransform: 'uppercase'}}>
                  Overview
                </span>
                {metricsLoading && (
                  <span style={{fontSize: '10px', color: '#d1d5db', marginLeft: 'auto'}}>Loading metrics...</span>
                )}
              </div>

              {/* ===== Top meta bar ===== */}
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '12px'}}>
                <div style={{fontSize: '12px', color: '#9ca3af', fontWeight: 500}}>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>

              {/* ===== KPI Grid (6 hero metrics) ===== */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px',
                marginBottom: '24px',
              }}>
                {kpis.map((kpi, idx) => (
                  <div
                    key={idx}
                    onClick={kpi.onClick}
                    title={kpi.full}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #f0f0f0',
                      borderRadius: '14px',
                      padding: '16px 18px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      cursor: kpi.onClick ? 'pointer' : 'default',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { if (kpi.onClick) { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#f0f0f0'; e.currentTarget.style.transform = 'none'; }}
                  >
                    <div style={{fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px'}}>
                      {kpi.label}
                    </div>
                    <div style={{fontSize: '24px', fontWeight: 700, color: '#1d4ed8', letterSpacing: '-0.02em', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                      {kpi.value}
                    </div>
                    {kpi.sub && (
                      <div style={{fontSize: '11px', color: '#9ca3af', marginTop: '6px', fontWeight: 500}}>
                        {kpi.sub}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* ===== Conversion Funnel + Breakdown ===== */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)',
                gap: '12px',
                marginBottom: '24px',
              }}>
                {/* Funnel */}
                <div style={{
                  background: '#ffffff',
                  border: '1px solid #f0f0f0',
                  borderRadius: '14px',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '16px'}}>
                    <div style={{fontSize: '13px', fontWeight: 700, color: '#111'}}>User Funnel</div>
                    <div style={{fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600}}>
                      {claimRate.toFixed(1)}% claimed
                    </div>
                  </div>

                  {/* Funnel rows — each bar width is proportional to totalUsers */}
                  {(() => {
                    // Real swipe count when available; fall back to null → hide the row gracefully
                    const swipedCount = usersWhoSwiped !== null ? usersWhoSwiped : 0;
                    const swipedPct = totalUsers > 0 ? (swipedCount / totalUsers) * 100 : 0;
                    return [
                      { label: 'Total Users', count: totalUsers, pct: 100, color: '#1d4ed8' },
                      { label: 'Have Swiped', count: swipedCount, pct: swipedPct, color: '#3b82f6' },
                      { label: 'Claimed', count: totalClaimedUsers, pct: claimRate, color: '#93c5fd' },
                    ];
                  })().map((row, i) => (
                    <div key={i} style={{marginBottom: i === 2 ? 0 : '12px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '11px'}}>
                        <span style={{color: '#6b7280', fontWeight: 600}}>
                          {row.label}
                          {row.note && <span style={{color: '#d1d5db', marginLeft: '6px', fontWeight: 500}}>({row.note})</span>}
                        </span>
                        <span style={{color: '#111', fontWeight: 700}}>
                          {fmt(row.count)} <span style={{color: '#9ca3af', fontWeight: 500, marginLeft: '4px'}}>{row.pct.toFixed(0)}%</span>
                        </span>
                      </div>
                      <div style={{
                        height: '8px',
                        background: '#f3f4f6',
                        borderRadius: '999px',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(100, Math.max(0, row.pct))}%`,
                          background: row.color,
                          borderRadius: '999px',
                          transition: 'width 0.4s ease',
                        }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* User Tier Distribution — donut chart (pure SVG) */}
                {(() => {
                  const tierOrder = ['platinum', 'gold', 'silver', 'bronze', 'flex'];
                  const tierColors = {
                    platinum: '#1e3a8a',
                    gold: '#1d4ed8',
                    silver: '#3b82f6',
                    bronze: '#93c5fd',
                    flex: '#dbeafe',
                  };
                  const tierLabels = {
                    platinum: 'Platinum',
                    gold: 'Gold',
                    silver: 'Silver',
                    bronze: 'Bronze',
                    flex: 'Flex',
                  };
                  const total = tierOrder.reduce((s, t) => s + (tierDistribution[t] || 0), 0);
                  // Donut geometry
                  const size = 180;
                  const cx = size / 2;
                  const cy = size / 2;
                  const r = 70;        // outer ring
                  const rInner = 46;   // hole

                  // Compute segments
                  let cumulative = 0;
                  const segments = tierOrder.map(tier => {
                    const count = tierDistribution[tier] || 0;
                    const pct = total > 0 ? count / total : 0;
                    const startAngle = cumulative * Math.PI * 2 - Math.PI / 2;
                    cumulative += pct;
                    const endAngle = cumulative * Math.PI * 2 - Math.PI / 2;
                    return { tier, count, pct, startAngle, endAngle };
                  });

                  // Arc path generator for a donut slice
                  const arcPath = (startAngle, endAngle) => {
                    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
                    const x1 = cx + r * Math.cos(startAngle);
                    const y1 = cy + r * Math.sin(startAngle);
                    const x2 = cx + r * Math.cos(endAngle);
                    const y2 = cy + r * Math.sin(endAngle);
                    const x3 = cx + rInner * Math.cos(endAngle);
                    const y3 = cy + rInner * Math.sin(endAngle);
                    const x4 = cx + rInner * Math.cos(startAngle);
                    const y4 = cy + rInner * Math.sin(startAngle);
                    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4} Z`;
                  };

                  return (
                    <div style={{
                      background: '#ffffff',
                      border: '1px solid #f0f0f0',
                      borderRadius: '14px',
                      padding: '20px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '16px'}}>
                        <div style={{fontSize: '13px', fontWeight: 700, color: '#111'}}>User Tiers</div>
                        <div style={{fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600}}>
                          {fmt(total)} classified
                        </div>
                      </div>

                      {total === 0 ? (
                        <div style={{fontSize: '12px', color: '#9ca3af', padding: '32px 0', textAlign: 'center'}}>
                          {metricsLoading ? 'Loading...' : 'No user tier data yet.'}
                        </div>
                      ) : (
                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px'}}>
                          {/* Donut */}
                          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                            {segments.map(seg => {
                              if (seg.count === 0) return null;
                              // If only one tier has data, render a full donut ring
                              if (seg.pct === 1) {
                                return (
                                  <g key={seg.tier}>
                                    <circle cx={cx} cy={cy} r={r} fill={tierColors[seg.tier]} />
                                    <circle cx={cx} cy={cy} r={rInner} fill="#ffffff" />
                                  </g>
                                );
                              }
                              return (
                                <path
                                  key={seg.tier}
                                  d={arcPath(seg.startAngle, seg.endAngle)}
                                  fill={tierColors[seg.tier]}
                                  stroke="#ffffff"
                                  strokeWidth="2"
                                />
                              );
                            })}
                            {/* Center total */}
                            <text x={cx} y={cy - 4} textAnchor="middle" style={{fontSize: '22px', fontWeight: 700, fill: '#111', letterSpacing: '-0.02em'}}>
                              {fmt(total)}
                            </text>
                            <text x={cx} y={cy + 14} textAnchor="middle" style={{fontSize: '9px', fontWeight: 600, fill: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase'}}>
                              Users
                            </text>
                          </svg>

                          {/* Legend */}
                          <div style={{width: '100%', display: 'flex', flexDirection: 'column', gap: '8px'}}>
                            {segments.map(seg => (
                              <div key={seg.tier} style={{display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px'}}>
                                <span style={{width: '10px', height: '10px', borderRadius: '3px', background: tierColors[seg.tier], flexShrink: 0}} />
                                <span style={{color: '#111', fontWeight: 600, flex: 1}}>{tierLabels[seg.tier]}</span>
                                <span style={{color: '#9ca3af', fontWeight: 500, fontSize: '11px'}}>
                                  {(seg.pct * 100).toFixed(0)}%
                                </span>
                                <span style={{color: '#111', fontWeight: 700, minWidth: '40px', textAlign: 'right'}}>
                                  {fmt(seg.count)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* ===== Bookmarked Pools — search + quick-glance column ===== */}
              {(() => {
                const q = poolSearch.trim().toLowerCase();
                const searchResults = q
                  ? allPools
                      .filter(p =>
                        (p.name || '').toLowerCase().includes(q) ||
                        (p.city || '').toLowerCase().includes(q) ||
                        (p.state || '').toLowerCase().includes(q)
                      )
                      .slice(0, 8)
                  : [];
                const loc = (p) =>
                  p && (p.city || p.state)
                    ? `${p.city || ''}${p.city && p.state ? ', ' : ''}${p.state || ''}`
                    : '';
                return (
                  <div style={{
                    background: '#ffffff',
                    border: '1px solid #f0f0f0',
                    borderRadius: '14px',
                    padding: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    marginBottom: '24px',
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '14px'}}>
                      <div style={{fontSize: '13px', fontWeight: 700, color: '#111'}}>Bookmarked Pools</div>
                      <div style={{fontSize: '11px', fontWeight: 600, color: '#9ca3af'}}>
                        {bookmarkedPools.length} saved
                      </div>
                    </div>

                    {/* Search box */}
                    <div style={{position: 'relative', marginBottom: bookmarkedPools.length ? '16px' : '4px'}}>
                      <input
                        type="text"
                        value={poolSearch}
                        onChange={(e) => setPoolSearch(e.target.value)}
                        placeholder="Search pools by name, city, or state…"
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '10px 12px',
                          fontSize: '13px',
                          fontFamily: 'inherit',
                          color: '#111',
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          outline: 'none',
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                      />

                      {/* Results dropdown */}
                      {q && (
                        <div style={{
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          left: 0,
                          right: 0,
                          zIndex: 20,
                          background: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '10px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                          overflow: 'hidden',
                        }}>
                          {searchResults.length === 0 ? (
                            <div style={{padding: '12px', fontSize: '12px', color: '#9ca3af'}}>
                              {allPools.length === 0 ? 'Loading pools…' : 'No pools match.'}
                            </div>
                          ) : (
                            searchResults.map(pool => {
                              const saved = isPoolBookmarked(pool._id);
                              return (
                                <div
                                  key={pool._id}
                                  onClick={() => toggleBookmarkPool(pool)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                  <div style={{flex: 1, minWidth: 0}}>
                                    <div style={{fontSize: '13px', fontWeight: 600, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                      {pool.name}
                                    </div>
                                    {loc(pool) && (
                                      <div style={{fontSize: '11px', color: '#9ca3af', marginTop: '1px'}}>{loc(pool)}</div>
                                    )}
                                  </div>
                                  <span style={{fontSize: '11px', fontWeight: 700, color: '#1d4ed8', minWidth: '48px', textAlign: 'right'}}>
                                    {pool.count} agent{pool.count === 1 ? '' : 's'}
                                  </span>
                                  <span style={{
                                    fontSize: '16px',
                                    lineHeight: 1,
                                    color: saved ? '#f59e0b' : '#d1d5db',
                                  }}>
                                    {saved ? '★' : '☆'}
                                  </span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>

                    {/* Bookmarked column */}
                    {bookmarkedPools.length === 0 ? (
                      <div style={{fontSize: '12px', color: '#9ca3af', padding: '4px 0'}}>
                        Search above and tap a pool to pin it here for quick access.
                      </div>
                    ) : (
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        {bookmarkedPools.map(bm => {
                          const live = allPools.find(p => p._id === bm._id);
                          const name = live?.name || bm.name;
                          const location = loc(live);
                          return (
                            <div
                              key={bm._id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 12px',
                                background: '#f9fafb',
                                border: '1px solid #f0f0f0',
                                borderRadius: '10px',
                              }}
                            >
                              <span style={{fontSize: '14px', lineHeight: 1, color: '#f59e0b'}}>★</span>
                              <div style={{flex: 1, minWidth: 0}}>
                                <div style={{fontSize: '13px', fontWeight: 600, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                  {name}
                                </div>
                                {location && (
                                  <div style={{fontSize: '11px', color: '#9ca3af', marginTop: '1px'}}>{location}</div>
                                )}
                              </div>
                              <span style={{fontSize: '12px', fontWeight: 700, color: '#1d4ed8', minWidth: '52px', textAlign: 'right'}}>
                                {live ? `${live.count} agent${live.count === 1 ? '' : 's'}` : '—'}
                              </span>
                              <button
                                onClick={() => toggleBookmarkPool(bm)}
                                title="Remove bookmark"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#9ca3af',
                                  fontSize: '16px',
                                  lineHeight: 1,
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                  fontFamily: 'inherit',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ===== Top Pools by Agent Count — vertical bar chart ===== */}
              <div style={{
                background: '#ffffff',
                border: '1px solid #f0f0f0',
                borderRadius: '14px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                marginBottom: '24px',
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '20px'}}>
                  <div style={{fontSize: '13px', fontWeight: 700, color: '#111'}}>Top Pools by Agent Count</div>
                  <button
                    onClick={() => setActiveTab('pools')}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#2563eb',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                      fontFamily: 'inherit',
                    }}
                  >
                    View all →
                  </button>
                </div>

                {topPools.length === 0 ? (
                  <div style={{fontSize: '12px', color: '#9ca3af', padding: '12px 0', textAlign: 'left'}}>
                    {metricsLoading ? 'Loading...' : 'No pools yet.'}
                  </div>
                ) : (() => {
                  const CHART_HEIGHT = 240;
                  // 40 bars won't fit narrow viewports — give the chart its own horizontal scroll.
                  const MIN_CHART_WIDTH = Math.max(600, topPools.length * 24 + 48);
                  return (
                    <div style={{overflowX: 'auto', overflowY: 'hidden', paddingBottom: '4px'}}>
                      <div style={{minWidth: `${MIN_CHART_WIDTH}px`}}>
                      {/* Y-axis max label */}
                      <div style={{display: 'flex', alignItems: 'flex-end', gap: '3px', height: `${CHART_HEIGHT + 4}px`, paddingLeft: '32px', position: 'relative', minWidth: 0}}>
                        {/* Y-axis gridlines */}
                        <div style={{position: 'absolute', left: 0, top: 0, bottom: '24px', right: 0, pointerEvents: 'none'}}>
                          {[1, 0.75, 0.5, 0.25, 0].map((frac, i) => (
                            <div key={i} style={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              top: `${(1 - frac) * 100}%`,
                              borderTop: i === 4 ? '1px solid #e5e7eb' : '1px dashed #f3f4f6',
                              display: 'flex',
                              alignItems: 'center',
                            }}>
                              <span style={{
                                fontSize: '9px',
                                color: '#d1d5db',
                                fontWeight: 600,
                                marginLeft: '-28px',
                                width: '24px',
                                textAlign: 'right',
                              }}>
                                {Math.round(maxPoolCount * frac)}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Bars */}
                        {topPools.map((pool, idx) => {
                          const heightPct = (pool.count / maxPoolCount) * 100;
                          const barHeight = (heightPct / 100) * (CHART_HEIGHT - 24);
                          return (
                            <div
                              key={pool._id}
                              style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                height: '100%',
                                minWidth: 0,
                                position: 'relative',
                                zIndex: 1,
                              }}
                              title={`${pool.name}${pool.city ? ` · ${pool.city}${pool.state ? `, ${pool.state}` : ''}` : ''}`}
                            >
                              {/* Value label above bar */}
                              <div style={{
                                fontSize: '9px',
                                fontWeight: 700,
                                color: '#1d4ed8',
                                marginBottom: '3px',
                              }}>
                                {pool.count}
                              </div>
                              {/* Bar */}
                              <div style={{
                                width: '100%',
                                maxWidth: '22px',
                                height: `${Math.max(2, barHeight)}px`,
                                background: 'linear-gradient(180deg, #2563eb, #1d4ed8)',
                                borderTopLeftRadius: '4px',
                                borderTopRightRadius: '4px',
                                transition: 'all 0.4s ease',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, #1d4ed8, #1e40af)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, #2563eb, #1d4ed8)'; }}
                              />
                            </div>
                          );
                        })}
                      </div>

                      {/* X-axis labels — pool names rotated 45° for density */}
                      <div style={{display: 'flex', gap: '3px', marginTop: '6px', paddingLeft: '32px', borderTop: '1px solid transparent', height: '90px', alignItems: 'flex-start'}}>
                        {topPools.map(pool => (
                          <div
                            key={`${pool._id}-label`}
                            style={{
                              flex: 1,
                              minWidth: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'flex-start',
                              paddingTop: '4px',
                            }}
                          >
                            <div
                              title={pool.name}
                              style={{
                                fontSize: '9px',
                                fontWeight: 600,
                                color: '#6b7280',
                                whiteSpace: 'nowrap',
                                transform: 'rotate(45deg)',
                                transformOrigin: 'left top',
                                maxWidth: '88px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {pool.name}
                            </div>
                          </div>
                        ))}
                      </div>
                      </div>
                    </div>
                  );
                })()}
              </div>


            </div>
          );
        })()}

        {/* Tab Content */}
        {activeTab === 'entry' && <ManualEntryTab onPropertyAdded={fetchPropertyCount} />}
        {activeTab === 'manage' && <ManagePropertiesTab onPropertyChanged={fetchPropertyCount} />}
        {activeTab === 'neighborhoods' && <NeighborhoodManagementTab />}
        {activeTab === 'agents' && <CreateAgentForm />}
        {activeTab === 'agent-metrics' && <AgentMetricsPanel />}
        {activeTab === 'pools' && <PoolManagement />}
        {activeTab === 'agent-listings' && <AgentListingsTab />}
        {activeTab === 'return-requests' && <ReturnRequestsPanel />}
        {activeTab === 'priority-queue' && <PriorityQueuePanel />}
        {activeTab === 'notification-queue' && <NotificationQueuePanel />}
        {activeTab === 'video-jobs' && <VideoJobsManager />}
        {activeTab === 'studio-campaigns' && <StudioCampaigns />}
        {activeTab === 'user-assignments' && <UserAssignmentManager />}
        {activeTab === 'user-metrics' && <UserMetricsPanel />}
        {activeTab === 'user-messages' && <AdminMessagesPanel />}
        {activeTab === 'agent-messages' && <AgentMessagesPanel />}
        {activeTab === 'conversations' && <AdminConversations />}
        {activeTab === 'agent-monitoring' && <AgentMonitoring />}
        {activeTab === 'messages' && <AdminMessaging />}
        {activeTab === 'curated-matches' && <CuratedMatchesManager />}
        {activeTab === 'condition-labeling' && <ConditionLabeler />}
        {activeTab === 'vision-test' && <VisionTest />}
        {activeTab === 'avm-readiness' && <AvmReadiness />}
        {activeTab === 'avm-estimate' && <AvmEstimate />}
        {activeTab === 'condition-by-metro' && <ConditionByMetro />}
        {activeTab === 'showings' && (
          <ShowingManagementTab 
            onShowingChanged={() => {
              fetchPropertyCount();
              fetchShowingCount();
            }} 
          />
        )}
        {activeTab === 'reviews' && (
          <ReviewListingsTab 
            onPropertyChanged={() => {
              fetchPropertyCount();
              fetchPendingReviewCount();
            }} 
          />
        )}
      </main>
    </div>
  );
};

export default PropertyAdminDashboard;